import { useRef, useState } from 'react';
import type { Screen } from './App';
import { useScrollLock } from './useScrollLock';
import { useSwipeDown } from './useSwipeDown';
import { useBackButton } from './useBackButton';
import type { Session } from './api';
import { logout } from './api';
import Icon, { type IconName } from './Icon';
import { LANG_LABEL, LANGS, useT } from './i18n';
import { useTheme, type ThemeMode } from './theme';
import { haptic, isTelegram } from './telegram';
import { useFeatures } from './features';
import { navFor } from './nav';

type Name = Screen['name'];

/**
 * Пять кнопок внизу — предел, после которого подписи начинают наезжать
 * друг на друга, а палец промахивается. Всё остальное уходит в «Меню».
 */
type Primary = { name: Name; icon: IconName; label: string };

const BASE: Primary[] = [
  { name: 'feed', icon: 'box', label: 'Грузы' },
  { name: 'orders', icon: 'route', label: 'Рейсы' },
  { name: 'profile', icon: 'user', label: 'Профиль' },
];

/**
 * Второе место в ряду — под главное действие, и оно у разных людей разное.
 *
 * Заказчик публикует заявку. Перевозчик и экспедитор объявляют свободную
 * машину — это их ежедневная работа, а заявки они публикуют редко или
 * никогда. Раздел при этом лежал за «Меню» и назывался иначе, чем его
 * собственный заголовок, — его просто не находили.
 */
function primaryFor(profile: Props['user']['profileType']): Primary[] {
  const carrier = profile === 'CARRIER' || profile === 'FORWARDER';
  const second: Primary = carrier
    ? { name: 'trucks', icon: 'truck', label: 'Машина' }
    : { name: 'publish', icon: 'plus', label: 'Заявка' };

  return [BASE[0]!, second, BASE[1]!, BASE[2]!];
}

interface Props {
  screen: Name;
  /** Сколько рейсов изменилось с последнего захода — цифра на вкладке. */
  freshOrders: number;
  isAdmin: boolean;
  isStaff: boolean;
  user: Session['user'];
  onGo: (name: Name) => void;
}

export default function MobileNav({
  screen,
  freshOrders,
  isAdmin,
  isStaff,
  user,
  onGo,
}: Props) {
  const [open, setOpen] = useState(false);
  const { mode, setMode } = useTheme();
  const { lang, t, setLang } = useT();
  const features = useFeatures();

  const sheet = useRef<HTMLDivElement>(null);
  const { drag, bind } = useSwipeDown(sheet, () => setOpen(false), { active: open });
  // Пока лист открыт — системная кнопка «назад» закрывает его, а не уводит
  // из приложения.
  useBackButton(open, () => setOpen(false));

  /*
   * «Ещё» — это всё, что не поместилось в нижний ряд.
   *
   * Считается вычитанием из общего списка разделов, а не вторым перечнем.
   * Второй перечень тут уже был, и он отстал: «Аналитика» осталась только
   * в боковой панели, а в Telegram раздела будто не существовало.
   */
  const primary = primaryFor(user.profileType);

  const extras = navFor({
    features,
    isAdmin,
    isStaff,
    profileType: user.profileType,
  })
    .flatMap((g) => g.items)
    .filter((i) => !primary.some((p) => p.name === i.name));

  const inMenu = extras.some((e) => e.name === screen);

  // Общее правило приложения: под открытым слоем страница не двигается.
  useScrollLock(open);

  function go(name: Name) {
    haptic();
    setOpen(false);
    onGo(name);
  }

  return (
    <>
      <nav className="mobnav">
        <button
          className={inMenu ? 'active' : ''}
          onClick={() => { haptic(); setOpen(true); }}
          aria-label={t('Меню')}
        >
          <Icon name="menu" />
          {t('Меню')}
        </button>

        {primary.map((tab) => (
          <button
            key={tab.name}
            data-tour={`nav-${tab.name}`}
            className={screen === tab.name ? 'active' : ''}
            onClick={() => go(tab.name)}
          >
            {/* Цифра висит на значке, а не на подписи: подписи в ряду
                разной длины, и значок — единственная точка, одинаковая у
                всех вкладок. */}
            <span className="mobnav__icon">
              <Icon name={tab.icon} />
              {tab.name === 'orders' && freshOrders > 0 && (
                <span className="navbadge navbadge--dot" aria-label={`Новых событий: ${freshOrders}`}>
                  {freshOrders > 9 ? '9+' : freshOrders}
                </span>
              )}
            </span>
            {t(tab.label)}
          </button>
        ))}
      </nav>

      {open && (
        <>
          {/* Закрытие по нажатию, а не по любому касанию: палец, ведущий
              по экрану, не должен захлопывать лист. */}
          <div className="sheet-backdrop" onClick={() => setOpen(false)} />
          <div
            className="sheet"
            role="dialog"
            ref={sheet}
            style={drag > 0 ? { transform: `translateY(${drag}px)` } : undefined}
            {...bind}
          >
            <div className="sheet__grip" aria-hidden />

            <div className="menu-user">
              <span className="avatar">
                {([user.firstName, user.lastName].filter(Boolean).join(' ') ||
                  user.login ||
                  '?')
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((w) => w[0]?.toUpperCase() ?? '')
                  .join('')}
              </span>
              <div>
                <strong>
                  {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                    user.login}
                </strong>
                <div className="meta">{user.isAdmin ? t('Администратор') : t('Перевозчик')}</div>
              </div>
            </div>

            <div className="menu-list">
              {extras.map((e) => (
                <button
                  key={e.name}
                  className={screen === e.name ? 'menu-item active' : 'menu-item'}
                  onClick={() => go(e.name)}
                >
                  <Icon name={e.icon} size={19} />
                  {t(e.label)}
                </button>
              ))}
            </div>

            <div className="divider" />

            {/* Взаимоисключающий выбор одного варианта — `.seg`, не
                `.chips`: тот же случай, что и с разделами админки. */}
            <div className="meta">{t('Тема')}</div>
            <div className="seg" style={{ margin: '8px 0 16px' }}>
              {(['system', 'light', 'dark'] as ThemeMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={mode === m ? 'seg__btn seg__btn--on' : 'seg__btn'}
                  onClick={() => setMode(m)}
                >
                  {t(m === 'light' ? 'Светлая' : m === 'dark' ? 'Тёмная' : 'Системная')}
                </button>
              ))}
            </div>

            <div className="meta">{t('Язык')}</div>
            <div className="seg" style={{ margin: '8px 0 16px' }}>
              {LANGS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={lang === l ? 'seg__btn seg__btn--on' : 'seg__btn'}
                  onClick={() => setLang(l)}
                >
                  {LANG_LABEL[l]}
                </button>
              ))}
            </div>

            {/* В Telegram аккаунт определяет мессенджер — выходить некуда. */}
            {!isTelegram && (
              <button
                className="secondary"
                onClick={() => {
                  logout();
                  window.location.reload();
                }}
              >
                <Icon name="logout" size={18} />
                {t('Выйти')}
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
